import React from 'react';
import cx from 'classnames';

import { ModalProvider, useModalContext } from '@magoosh/context/modal_context';
import { formatPrice } from 'utilities/money';

import { Col, Collapse, Grid, Row } from 'react-bootstrap';
import { DefaultButton, SecondaryButton } from '@magoosh/lib/button';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import Confirmation from './confirmation';

import { AddOnOffer } from './types';

import HeadshotEric from './assets/mba_admissions/headshot_eric.png';
import HeadshotJen from './assets/mba_admissions/headshot_jen.png';
import HeadshotVeronica from './assets/mba_admissions/headshot_veronica.png';
import colors from '@magoosh/lib/styles/colors.module.scss';
import style from './style.module.scss';
import { paths } from 'config/path_helpers';

const CLASS_DETAILS = [
  {
    description:
      'With decades of combined experience working with students and b-schools, our diverse team of admissions experts share the deep knowledge you need to put your best foot forward, including how to build an MBA brand, mitigate your weaknesses, craft a compelling resume, and more.',
    name: 'Expert Advice from Admissions Insiders'
  },
  {
    description:
      'Our engaging and in-depth video walkthroughs guide you through the entire application process, from choosing the right schools to preparing for your interviews.',
    name: '30+ MBA Application Videos'
  },
  {
    description:
      'Got questions? Reach out to our Help team for quick email support from an MBA Admissions expert.',
    name: 'Ask an Expert'
  },
  {
    description:
      'Full access to Magoosh MBA Admissions for 12 months. Pause your plan at any time if your application plans change.',
    name: '12 Months of Access'
  }
];

const AdmissionsAddOn: React.FC<AddOnOffer> = (props) => {
  const { discountedPrice, fullPrice } = props.price;
  const { setModal } = useModalContext();

  const handleAddOnPurchaseClicked = () => {
    if (props.paymentDetails) {
      setModal({
        header: 'Add MBA Admissions Support',
        body: (
          <Confirmation
            addOnName="MBA Admissions Support"
            handleClose={() => setModal(null)}
            successMessage='You can access your MBA Admissions videos using the "Admissions" button in your GRE dashboard.'
            {...props}
          />
        )
      });
    } else {
      window.location.href = `${paths.subscriptions(props.orderItem.purchasable.planSlug)}?coupon=${
        props.price.couponCode
      }`;
    }
  };

  return (
    <Grid className="u-padding-T-m u-padding-B-xl">
      <Row>
        <Col md={6} mdOffset={3}>
          <h4 className="text-gray">One-Time Offer</h4>
          <h2 className="u-margin-T-n">
            Add MBA Admissions Support for{' '}
            {discountedPrice !== fullPrice && (
              <>
                <span style={{ textDecoration: 'line-through' }}>{formatPrice(fullPrice, 'usd', true)}</span>{' '}
              </>
            )}
            {formatPrice(discountedPrice, 'usd', true)}!
          </h2>
        </Col>
      </Row>

      <Row>
        <Col md={6} mdOffset={3}>
          <hr className="u-margin-T-n" />

          <p>Our Admissions experts will walk you through the entire business school application process.</p>

          {discountedPrice !== fullPrice && (
            <p>
              <strong>Plus - if you sign up now, you’ll get a big discount</strong> (regular price is{' '}
              {formatPrice(fullPrice, 'usd', true)})!
            </p>
          )}

          <div className={cx('d-flex u-margin-V-l', style.ctas)}>
            <SecondaryButton onClick={handleAddOnPurchaseClicked}>
              Yes, Add <span className="hidden-xs">MBA Admissions Support</span> for{' '}
              {formatPrice(discountedPrice, 'usd', true)}
            </SecondaryButton>

            {props.nextPath && (
              <DefaultButton className="u-margin-L-s" href={props.nextPath}>
                No Thanks
              </DefaultButton>
            )}
          </div>

          <div
            className="border-radius-all u-margin-V-l u-padding-A-m"
            style={{ backgroundColor: '#F5EEFC' }}>
            <p className="text-uppercase">
              <strong>MBA ADMISSIONS SUPPORT INCLUDES:</strong>
            </p>

            {CLASS_DETAILS.map((classDetail, idx) => (
              <ClassDetail key={idx} classNumber={idx + 1} {...classDetail} />
            ))}
          </div>

          <div className="u-margin-T-xl">
            <div className="u-margin-B-s">
              <strong>
                <u>Meet the Magoosh Admissions Teaching Team</u>
              </strong>
            </div>

            <div className="d-flex justify-content-between">
              <div>
                <img
                  alt="Derrick Photo"
                  className="img-circle"
                  src={HeadshotEric}
                  style={{ objectFit: 'cover', height: 100, width: 100 }}
                />
                <div className="u-margin-T-s small text-center">Eric Allen</div>
              </div>

              <div>
                <img
                  alt="Taryn Photo"
                  className="img-circle"
                  src={HeadshotJen}
                  style={{ objectFit: 'cover', height: 100, width: 100 }}
                />
                <div className="u-margin-T-s small text-center">Jen Jurgens</div>
              </div>

              <div>
                <img
                  alt="Linnea Photo"
                  className="img-circle"
                  src={HeadshotVeronica}
                  style={{ objectFit: 'cover', height: 100, width: 100 }}
                />
                <div className="u-margin-T-s small text-center">Veronica Mandujano</div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Grid>
  );
};

interface ClassDetailProps {
  classNumber: number;
  description: string;
  name: string;
}

const ClassDetail: React.FC<ClassDetailProps> = ({ classNumber, description, name }) => {
  const [isExpanded, setIsExpanded] = React.useState(classNumber === 1);

  return (
    <div className="u-margin-T-s">
      <div onClick={() => setIsExpanded(!isExpanded)} style={{ cursor: 'pointer' }}>
        <strong>
          <span className="u-margin-R-xs" style={{ color: colors.brandFuchsia }}>
            <span className="u-margin-R-xxs">
              <FontAwesomeIcon icon={isExpanded ? 'minus' : 'plus'} />
            </span>
            <span>{name}</span>
          </span>
        </strong>
      </div>

      <Collapse in={isExpanded}>
        <div>{description}</div>
      </Collapse>
    </div>
  );
};

const AdmissionsAddOnWithProvider: React.FC<AddOnOffer> = (props) => {
  return (
    <ModalProvider>
      <AdmissionsAddOn {...props} />
    </ModalProvider>
  );
};

export default AdmissionsAddOnWithProvider;
